import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>The previous Lessons treated the loudspeaker as if it were a linear system. Although linear modeling is very useful, especially because of the easy-to-apply mathematical tools, the actual loudspeaker behaves like a nonlinear device. At low signal levels, the linear description is usually sufficient. However, at high signal levels, the linear model deviates greatly from the actual behavior of the loudspeaker and the nonlinear approach must be used [2].
<p>Loudspeaker nonlinearities are the main cause of distortion, which can significantly impact sound quality in audio reproduction. Since loudspeakers have the highest distortion levels within the audio chain, it is essential to keep them within acceptable limits. Therefore, understanding the causes of nonlinearities and how to reduce them is crucial for achieving good sound reproduction quality. This Lesson aims to provide an overview of some well-known nonlinear phenomena that can often be encountered when studying loudspeakers.
<h3 class=sectionHead>Force factor</h3>
<p>
   The force factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   represents the integral of the flux density
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
   </math>
   in space over the length of the voice-coil
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ℓ</mi>
   </math>. Since the flux density
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
   </math>
   is not uniform but depends on the position of the voice-coil, and since the voice-coil is of finite length, the force factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   depends on the voice-coil position
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>. This dependence is very intuitive: when the moving coil leaves the gap, the flux density seen by the coil is much lower and the force factor decreases. A common way to model this nonlinear behavior is to use a polynomial representation
   <span style="font-size: 14px">
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>B</mi>
      <msub>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>B</mi>
      <msub>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>B</mi>
      <msub>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <mo class=MathClass-rel>⋯</mo>
   </math>
   </span>. Note, that nonlinearities linked to force factor are a major source of low-frequency harmonic distortion, as well as intermodulation distortion when combining bass and treble sounds.
<p>
   The shape of the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   curves depends on both the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
   </math>
   field in space and the height of the moving coil. If the height of the moving coil is equal to the height of the pole piece, the force factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is maximal at the rest position (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>) but decreases when the moving coil leaves the rest position. To obtain a flat
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   curve around the rest position and, therefore, a larger linear region, a low or high height voice-coil can be used. For voice-coils of low height relative to the height of the pole piece (underhung configuration), a very flat
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   curve can be obtained at the cost of a lower force factor due to the short length of the voice-coil. Higher voice-coils (overhang configuration) can achieve higher values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   at rest while maintaining a flat curve of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>, but it comes at the cost of increased mass and increased 
       <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>e</mi>
         </mrow>
      </msub>
   </math> due to the longer and thus heavier voice-coil. All three configurations are shown in <a href=# data-img-src=${config.ASSETS_URL}/04/26/theory/figure-1.svg data-img-alt="Figure 1. Force factor as a function of coil position.">Figure 1</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/26/theory/figure-1.svg alt="Figure 1. Force factor as a function of coil position."><figcaption class=caption>Figure 1. Force factor as a function of coil position.</figcaption></figure>
<p> A common parameter provided by loudspeaker manufacturers in their datasheets and specifications is 
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mn>max</mn>
         </mrow>
      </msub>
   </math>, which refers to the maximum distance that the diaphragm can move in a linear manner without causing distortion (typically 10% distortion) or damage to the speaker. When considering only the force factor, 10% distortion is associated with the 
 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mn>max</mn><mi mathvariant=italic>Bℓ</mi>
         </mrow>
      </msub>
   </math> 
    value. This value is defined as the displacement at which the 
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
   </math> value drops to 82% of its value at the rest position.
<h3 class=sectionHead>Stiffness</h3>
<p>
   Loudspeaker suspensions are usually made of rubber, impregnated fabric, or molded plastic. They are used to center the coil in the air gap and provide a restoring force. As seen in previous Lessons, the moving mechanical part of a loudspeaker is modeled by a mass-spring system. The suspensions is represented by spring with a stiffness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>. In the linear approximation, the restoring force is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mi>x</mi>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   being the displacement of the membrane. However, the actual suspensions can not behave linearly for several reasons. First, as the suspension elongates with increasing coil position it can reach a point where a further increase of elongation requires a much larger force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
   </math>
   than the one predicted by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mi>x</mi>
   </math>. As a consequence, since one needs more and more force to reach higher values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>, the stiffness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   seems to depend on
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>. This leads to a restoring force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mi>x</mi>
   </math>. An example of such a restoring force and displacement-dependent stiffness is shown in <a href=# data-img-src=${config.ASSETS_URL}/04/26/theory/figure-2.svg data-img-alt="Figure 2. Force (above) and stiffness (below) as a function of coil position.">Figure 2</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/26/theory/figure-2.svg alt="Figure 2. Force (above) and stiffness (below) as a function of coil position."><figcaption class=caption>Figure 2. Force (above) and stiffness (below) as a function of coil position.</figcaption></figure>
<p>
   The simplest way to model this behavior is, as in the previous case with the force factor, to represent the displacement dependent stiffness as a polynomial
   <span style="font-size: 14px">
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <mo class=MathClass-rel>⋯</mo>
   </math>
   </span>.
<p>
   Another reason for nonlinear behavior is related to the creep effect mentioned in the previous Lesson dealing with the limits of the linear model [3]. Indeed, the creep effect is very dependent on the displacement. The higher the displacement, the higher the elongation and local damage of the polymer chains inside the visco-elastic materials. As a consequence the nonlinear stiffness curve
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can also depend on the history of the elongation [1]. If the loudspeaker cone displacement is large (e.g. due to a loud low-frequency music) before time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>t</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>, the stiffness
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   measured with low signal after
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>t</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   may be lower by several tens of percents compared to a situation where there was no stimuli before time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>t</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>.
    
<p>Note, that stiffness-related nonlinearities 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math> can be one of the main sources of low-frequency harmonic distortion in loudspeakers. This phenomenon is also referred to as "compliance distortion" (compliance 
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo>=</mo>
            <msub>
         <mrow>
            <mi>1/K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>). To determine maximal excursion before reaching 10% distortion 
 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mn>max</mn>
            <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
         </mrow>
      </msub>
   </math> value. This value is defined as the displacement at which the 
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
 value drops to 75% of its value at the rest position.

<p>Loudspeaker manufacturers use various design techniques and materials to optimize the suspension system's behavior and minimize nonlinearity. These may include selecting appropriate suspension materials, designing effective spiders and surrounds, for example, using a spider or surround with a higher number of rolls.
<h3 class=sectionHead>Coil inductance, flux modulation, and hysteresis</h3>
<h4 class=sectionHead>Inductance and resistance</h4>
<p>
   The inductance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   and resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   of the voice coil can be position and current dependent. This dependence is usually associated with two physical effects: magnetic permeability
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>μ</mi>
   </math>
   of pole pieces and edddy currents [7]. In a traditional electrodynamic motor, in which the pole pieces are made of ferromagnetic materials, both effects play an important role.
<p>
   The permeability
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>μ</mi>
   </math>
   of a ferromagnetic material is much higher than that of air. Since the voice-coil inductance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   is proportional to the permeability
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>μ</mi>
   </math>, the coil inductance depends largely on voice-coil position. When the coil is immersed in the motor (position (1) in <a href=# data-img-src=${config.ASSETS_URL}/04/26/theory/figure-3.svg data-img-alt="Figure 3. Inductance of a voice-coil as a function of position <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>x</mi></math>.">Figure 3</a>) the amount of iron around and inside the voice-coil is very high. Consequently, the mean permeability seen by the voice-coil is high leading to a larger inductance. As we move the voice-coil out of the motor (position (5) in <a href=# data-img-src=${config.ASSETS_URL}/04/26/theory/figure-3.svg data-img-alt="Figure 3. Inductance of a voice-coil as a function of position <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>x</mi></math>.">Figure 3</a>) the mean permeability seen by the voice-coil drops significantly compared to the previous case and consequently the inductance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   drops too.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/26/theory/figure-3.svg alt="Figure 3. Inductance of a voice-coil as a function of position <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>x</mi></math>."><figcaption class=caption>Figure 3. Inductance of a voice-coil as a function of position <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>x</mi></math>.</figcaption></figure>
<h4 class=sectionHead>Flux modulation and magnetic hysteresis</h4>
<p>
   Flux modulation is another phenomenon that plays an important role in nonlinear behavior of an electrodynamic loudspeaker [6, 5]. The resulting indirect effects are variation of both the force factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Bℓ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>i</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and inductance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>i</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   with current
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
   </math>. Since there is an alternating current
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
   </math>
   flowing in the voice-coil, there is also an alternating magnetic flux induced by this current. This alternating magnetic flux enters the pole piece and top plate material in the air gap vicinity and is added to the static flux density
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   caused by the permanent magnets. As a consequence, the static magnetic flux (due to magnets) is modulated by the alternating magnetic flux (due to current in the voice-coil). As the pole piece and top plate are made of ferromagnetic materials which exhibit magnetic hysteresis, the flux modulation depends on this hysteresis.
<p>
   This phenomena is demonstrated in <a href=# data-img-src=${config.ASSETS_URL}/04/26/theory/figure-4.svg data-img-alt="Figure 4. Typical magnetisation B-H curve of ferromagnetic material.">Figure 4</a> where a typical magnetisation B-H curve of ferromagnetic material is depicted with the hysteresis loop. The black line is anhysteretic on which is plotted a red hysteretic loop that follows different path depending on the direction of change of the magnetic field intensity. The permanent magnet creates a static magnetic flux
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   in the air gap and in the pole piece and top plate material near the air gap. An alternating voice-coil current
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
   </math>
   creates an alternating magnetic filed intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>H</mi>
   </math>
   that may cause a variation of the magnetic flux
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
   </math>. It is important to note that this phenomena depends also on the geometry and shape of the ferromagnetic materials used in the motor (mainly the pole pieces and top plate) [4].
<p>
   Another serious consequence is the variation of voice-coil inductance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>. As the latter depends on the permeability
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>μ</mi>
   </math>
   which is defined as the slope of the hysteretic B-H curve, the permeability varies with the voice-coil current
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
   </math>. Consequently, one can observe variation of voice-coil inductance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>i</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   with current.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/26/theory/figure-4.svg alt="Figure 4. Typical magnetisation B-H curve of ferromagnetic material."><figcaption class=caption>Figure 4. Typical magnetisation B-H curve of ferromagnetic material.</figcaption></figure>
<h4 class=sectionHead>Shorting rings and copper cups</h4>
<p>The above phenomena, such as position-dependent loudspeaker inductance <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mo>(</mo>
    <msub>
         <mrow>
        <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
     <mo>)</mo>
   </math> and the associated flux modulation and hysteresis, are significant sources of distortion in loudspeakers. To reduce the negative effects and improve linearity, manufacturers use shorting rings or copper caps that are constructed from conductive materials. Consequently, they both facilitate the flow of eddy currents within them. These eddy currents generate magnetic fields that oppose the magnetic field produced by the voice coil.

<p>Shorting rings (also called demodulation rings), typically positioned either beneath the top plate or above the central pole, are particularly prevalent in subwoofers, woofers, and midranges. These rings play a crucial role in diminishing the value of inductance <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mo>(</mo>
    <msub>
         <mrow>
        <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
     <mo>)</mo>
   </math>.

<p>On the other hand, copper cups, positioned on the pole piece and within the magnetic gap, are more commonly employed in midranges and tweeters. Unlike shorting rings, copper cups have a more focused objective, which includes modifying the shape of 
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msub>
         <mrow>
        <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
    <mo>(</mo><mi>x</mi>
     <mo>)</mo>
   </math>, in addition to potentially lowering the overall inductance value. However, a noteworthy drawback of copper cups is that they occupy space within the magnetic gap, resulting in the gap becoming magnetically larger, as a portion of the iron is replaced by copper.
<h3 class=likesectionHead>Conclusion</h3>
<p>Understanding loudspeaker nonlinearities is essential for achieving high-quality sound reproduction. While linear models work well at low signal levels, loudspeakers behave as nonlinear devices at high signal levels, leading to distortion and compromised sound quality. Force factor nonlinearity, stiffness-related nonlinearity, and nonlinearities related to coil inductance and flux modulation are significant sources of distortion.

<p>One important parameter in this context is 
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mn>max</mn>
         </mrow>
      </msub>
   </math>, which imposes a limit on the maximum linear Sound Pressure Level (SPL) achievable. Highly nonlinear speakers tend to have low 
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mn>max</mn>
         </mrow>
      </msub>
   </math> values. With such low values, it becomes challenging to achieve a high SPL while maintaining high sound quality. In contrast, high-performance speakers have 
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>X</mi>
         </mrow>
         <mrow>
            <mn>max</mn>
         </mrow>
      </msub>
   </math> which enable significant excursions while keeping distortion within acceptable limits.

<p>Manufacturers employ various techniques, such as underhung / overgung configuration, selecting appropriate suspension materials, using shorting rings or copper cups, to weaken all these nonlinear effects and improve loudspeaker linearity. By addressing these nonlinearities, we can enhance the performance of loudspeakers and deliver more accurate and distortion-free audio reproduction, ensuring a better listening experience.


    
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>SPL
  <dd>Sound Pressure Level
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-King2018 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Alexander King and Finn T. Agerkvist. “Fractional derivative loudspeaker models for nonlinear suspensions and voice coils”. In: <span class=cmti-10>J. Audio Eng. Soc.</span> 66.7-8 (2018), pp. 525–536.
   <dt id=X0-klippel2006tutorial class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Wolfgang Klippel. “Tutorial: Loudspeaker Nonlinearities—Causes, Parameters, Symptoms”. In: <span class=cmti-10>J. Audio Eng. Soc </span>54.10 (2006), pp. 907–939.
   <dt id=X0-Knudsen1993 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Morten H. Knudsen and J. Grue Jensen. “Low-Frequency Loudspeaker Models That Include Suspension Creep”. In: <span class=cmti-10>J. Audio Eng. Soc. </span>41.1/2 (1993), pp. 3–18.
   <dt id=X0-baratelli2020predicting class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p>M. Baratelli and O. Munroe. “Predicting loudspeaker current distortion with FEA”. In: <span class=cmti-10>Audio Engineering Society Convention 148</span>. Audio Engineering Society. 2020.
   <dt id=X0-HalvorsenMorten2015 class=thebibliography>[5]
   <dd id=bib-5 class=thebibliography>
      <p>M. Halvorsen, C. Tinggaard and F. T. Agerkvist. “Flux Modulation in the Electrodynamic Loudspeaker”. In: <span class=cmti-10>Audio Eng. Soc. Conv. 138</span>. 2015.
   <dt id=X0-VictorMazin1999 class=thebibliography>[6]
   <dd id=bib-6 class=thebibliography>
      <p>V. Mazin. “Modeling of Magnetic Hysteresis and its influence on harmonic distortion in electrodynamic loudspeakers”. In: <span class=cmti-10>Audio Eng. Soc. Conv. 106</span>. 1999.
   <dt id=X0-Vanderkooy1989 class=thebibliography>[7]
   <dd id=bib-7 class=thebibliography>
      <p>John Vanderkooy. “A Model of Loudspeaker Driver Impedance Incorporating Eddy Currents in the Pole Structure”. In: <span class=cmti-10>J. Audio Eng. Soc.</span> 37.3 (1989), pp. 119–128.
</dl>`;

export default theory;
